type Entry = {
  id: string;
  title: string;
  type: string;
  revision: string;
  summary: string;
  tone: string;
  focusChipId?: string;
};

const baseEntries: Entry[] = [
  {
    id: "CPU-001",
    title: "Game Two CPU Encounter",
    type: "ACTIVE CHIP",
    revision: "REV 12",
    summary: "Resolve visual identity and seed the first vertical slice.",
    tone: "cyan",
    focusChipId: "cpu-001",
  },
  {
    id: "VIS-014",
    title: "DeckOS Visual Identity",
    type: "IMPRINT",
    revision: "REV 4",
    summary: "V3KTOR defines the approved default visual language.",
    tone: "gold",
  },
  {
    id: "LAW-003",
    title: "Route Semantic Law",
    type: "IMPRINT",
    revision: "REV 2",
    summary:
      "Route color, geometry, and line state preserve operational meaning.",
    tone: "gold",
  },
  {
    id: "ARC-021",
    title: "Sector 47 Interface Notes",
    type: "ARCHIVE",
    revision: "REV 7",
    summary: "Inactive interface research retained for historical reference.",
    tone: "muted",
  },
];

function Icon({ tone, id }: { tone: string; id: string }) {
  return (
    <svg
      class={`knowledge-icon ${tone}`}
      viewBox="0 0 32 32"
      aria-hidden="true"
    >
      {id === "CPU-001" && (
        <path d="M16 4 25 9v10l-9 5-9-5V9zM16 14l9-5M16 14 7 9M16 14v10" />
      )}
      {id.startsWith("LORE-") && (
        <path d="M7 9h18v16H7zM10 6h12M10 13h12M10 18h8" />
      )}
      {id === "VIS-014" && (
        <path d="m5 11 11-6 11 6-11 6zM5 16l11 6 11-6M5 21l11 6 11-6" />
      )}
      {id === "LAW-003" && (
        <path d="M16 4v24M4 16h24M7 7h4v4H7zM21 21h4v4h-4z" />
      )}
      {id === "ARC-021" && <path d="M7 9h18v16H7zM10 6h12M10 13h12M10 18h8" />}
    </svg>
  );
}

type Props = {
  onSelectChip?: (id: string) => void;
  /** Module fiction unlocks — always labeled non-authoritative. */
  loreEntries?: Entry[];
  gamebookOnly?: boolean;
};

export function ArcopediaScreen({
  onSelectChip,
  loreEntries = [],
  gamebookOnly = false,
}: Props) {
  const entries = gamebookOnly
    ? loreEntries
    : [...loreEntries, ...baseEntries];

  return (
    <section class="arcopedia-screen" aria-labelledby="arcopedia-title">
      <div class="arcopedia-heading">
        <span class="eyebrow">ACTIVE KNOWLEDGE</span>
        <h1 id="arcopedia-title">ARCOPEDIA</h1>
      </div>
      <label class="arcopedia-search">
        <span aria-hidden="true">⌕</span>
        <input aria-label="Search Arcopedia" placeholder="SEARCH ARCOPEDIA" />
      </label>
      <div class="filter-row arcopedia-filters">
        <button class="filter deck-chamfer-button active">CHIPS</button>
        <button class="filter deck-chamfer-button">IMPRINTS</button>
        <button class="filter deck-chamfer-button">ARCHIVE</button>
      </div>
      <div class="knowledge-list">
        {entries.length === 0 && (
          <p class="meta">NO UNLOCKED MODULE LORE</p>
        )}
        {entries.map((entry, index) => (
          <article
            class={`knowledge-row ${index === 0 ? "selected" : ""}`}
            key={entry.id}
          >
            <button
              class={`knowledge-select deck-octagon ${entry.tone}`}
              onClick={() =>
                entry.focusChipId && onSelectChip?.(entry.focusChipId)
              }
              aria-label={`Open ${entry.id} in CHIPMAP`}
            >
              <Icon id={entry.id} tone={entry.tone} />
            </button>
            <div class="knowledge-copy">
              <div class="knowledge-top">
                <strong>{entry.id}</strong>
                <span>{entry.revision}</span>
              </div>
              <h2>{entry.title}</h2>
              <div class="knowledge-type">{entry.type}</div>
              <p>{entry.summary}</p>
              {entry.type.includes("FIXTURE") &&
                !entry.summary.includes("DEVELOPMENT FIXTURE") && (
                <p class="meta">DEVELOPMENT FIXTURE / NON-AUTHORITATIVE</p>
              )}
              {entry.focusChipId && (
                <button
                  class="focus-chipmap deck-chamfer-button"
                  onClick={() => onSelectChip?.(entry.focusChipId!)}
                >
                  FOCUS IN CHIPMAP
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
      <div class="result-count">{entries.length} ENTRIES</div>
    </section>
  );
}
